import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  doc,
  onSnapshot,
  setDoc,
  Timestamp,
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface FeatureFlags {
  merchantOnboarding: boolean;
  clientOnboarding: boolean;
  clientBusiness: boolean;
  workflows: boolean;
  monoManagement: boolean;
  kybMerchants: boolean;
  updatedAt?: Timestamp;
  updatedBy?: string;
}

export const DEFAULT_FLAGS: FeatureFlags = {
  merchantOnboarding: true,
  clientOnboarding: true,
  clientBusiness: true,
  workflows: true,
  monoManagement: true,
  kybMerchants: true,
};

/**
 * FeatureFlagsService
 * Manages feature flags stored in Firestore `config/feature-flags`.
 * Admins can toggle pages on/off from the admin panel.
 * Flags are streamed in real-time via onSnapshot.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
  private firestore = inject(Firestore);
  private auth = inject(Auth);

  private readonly configDocPath = 'config/feature-flags';

  private flagsSubject = new BehaviorSubject<FeatureFlags>(DEFAULT_FLAGS);
  readonly flags$: Observable<FeatureFlags> = this.flagsSubject.asObservable();

  constructor() {
    this.startListening();
  }

  /** Stream feature flags from Firestore in real-time */
  private startListening(): void {
    const docRef = doc(this.firestore, this.configDocPath);
    onSnapshot(
      docRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() as Partial<FeatureFlags>;
          this.flagsSubject.next({ ...DEFAULT_FLAGS, ...data });
        } else {
          this.flagsSubject.next(DEFAULT_FLAGS);
        }
      },
      (err) => {
        console.warn('FeatureFlagsService: could not read flags, using defaults', err);
        this.flagsSubject.next(DEFAULT_FLAGS);
      }
    );
  }

  /** Get current flags snapshot (synchronous) */
  getFlags(): FeatureFlags {
    return this.flagsSubject.getValue();
  }

  /** Observable for a single flag */
  flag$(key: keyof Omit<FeatureFlags, 'updatedAt' | 'updatedBy'>): Observable<boolean> {
    return this.flags$.pipe(map((f) => f[key] as boolean));
  }

  /** Save all feature flags. Requires ADMIN role (enforced by Firestore rules). */
  async saveFlags(flags: Omit<FeatureFlags, 'updatedAt' | 'updatedBy'>): Promise<void> {
    const user = this.auth.currentUser;
    const docRef = doc(this.firestore, this.configDocPath);
    await setDoc(docRef, {
      ...flags,
      updatedAt: Timestamp.now(),
      updatedBy: user?.uid ?? 'unknown',
    });
  }

  /** Toggle a single flag */
  async toggleFlag(
    key: keyof Omit<FeatureFlags, 'updatedAt' | 'updatedBy'>,
    value: boolean
  ): Promise<void> {
    const current = this.getFlags();
    const updated = { ...current, [key]: value };
    await this.saveFlags(updated);
  }
}
